import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { MdClose, MdAdd, MdCheck } from "react-icons/md";
import { RxOpenInNewWindow } from "react-icons/rx";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { thunks as groupSvc } from "src/thunks/group";
import { thunks as regionSvc } from "src/thunks/region";
import { DataRanges } from "src/utils/configs";
import { enrichRegions } from "src/utils/enrich-regions";
import { RegionChartCard } from "src/pages/dashboard/home/region-chart-card";
import { paths } from "src/paths";

/**
 * 그룹의 selectedSidos/Gungus/Dongs 중 dataRange에 맞는 배열 키.
 */
const fieldByDataRange = (dataRange) => {
  if (dataRange === DataRanges.SIDO) return "selectedSidos";
  if (dataRange === DataRanges.GUNGU) return "selectedGungus";
  return "selectedDongs";
};

/** 선택된 지역 새창 열기 — id와 dataRange를 쿼리로 전달 */
const openRegionNewWindow = (regionId, dataRange) => {
  if (!regionId || !dataRange) return;
  const url = `${paths.dashboard.regions.newwindow}?id=${regionId}&range=${dataRange}&menu=false`;
  window.open(url, "_blank", `width=1200,height=${window.innerHeight}`);
};

/**
 * 관심지역 등록 다이얼로그.
 * - SIDO/GUNGU/DONG 단위 region을 그룹의 selectedSidos/Gungus/Dongs에 등록/제거.
 * - 데스크톱: 좌측 그룹 리스트 + 우측 컨텐츠
 * - 모바일: 상단 그룹 dropdown + 컨텐츠 풀폭
 */
export const FavoriteRegionDialog = ({
  open,
  onClose,
  regionId,
  regionName,
  dataRange,
  parentName,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const regionGroups = useSelector((state) => state.group.regionGroups || []);
  // sidos store — 이름/부모/인구수 fallback에 사용 (특히 동 이름 표시)
  const { sidos, sidosInit } = useSelector((state) => state.region);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [newGroupName, setNewGroupName] = useState("");
  const [groupRegions, setGroupRegions] = useState([]);
  const [regionsLoading, setRegionsLoading] = useState(false);
  // 선택된 region의 상세 정보 (헤더 아래 카드로 표시) — sidos store + enrichRegions 활용
  const [selectedRegion, setSelectedRegion] = useState(null);

  const field = fieldByDataRange(dataRange);

  useEffect(() => {
    if (open) {
      dispatch(groupSvc.getRegionGroups());
      // sidos가 아직 로드 안 됐으면 fetch — 동 이름/부모 fallback에 필요
      if (!sidosInit) dispatch(regionSvc.getSidos());
    }
  }, [open, dispatch, sidosInit]);

  useEffect(() => {
    if (open && regionGroups.length > 0 && selectedGroupId == null) {
      setSelectedGroupId(regionGroups[0].id);
    }
  }, [open, regionGroups, selectedGroupId]);

  useEffect(() => {
    if (!open) {
      setSelectedGroupId(null);
      setNewGroupName("");
      setGroupRegions([]);
      setSelectedRegion(null);
    }
  }, [open]);

  // 선택된 region을 enrichRegions로 변환해 카드 표시용 데이터 생성.
  // 단일 region을 group 형태로 감싸 enrichRegions 재사용.
  useEffect(() => {
    if (!open || !regionId || !dataRange || !sidosInit) {
      setSelectedRegion(null);
      return;
    }
    let cancelled = false;
    const singleGroup = {
      selectedSidos: dataRange === DataRanges.SIDO ? [regionId] : [],
      selectedGungus: dataRange === DataRanges.GUNGU ? [regionId] : [],
      selectedDongs: dataRange === DataRanges.DONG ? [regionId] : [],
    };
    (async () => {
      try {
        const enriched = await enrichRegions(singleGroup, { sidos });
        if (!cancelled) setSelectedRegion(enriched?.[0] || null);
      } catch {
        if (!cancelled) setSelectedRegion(null);
      }
    })();
    return () => { cancelled = true; };
  }, [open, regionId, dataRange, sidos, sidosInit]);

  const selectedGroup = useMemo(
    () => regionGroups.find((g) => g.id === selectedGroupId) ?? null,
    [regionGroups, selectedGroupId],
  );

  const isInSelectedGroup = useMemo(() => {
    if (!selectedGroup || !regionId) return false;
    return (selectedGroup[field] || []).includes(regionId);
  }, [selectedGroup, regionId, field]);

  // 그룹 안의 region 멤버 카운트 (전체)
  const groupRegionCount = (g) =>
    (g.selectedSidos || []).length +
    (g.selectedGungus || []).length +
    (g.selectedDongs || []).length;

  // 선택된 그룹의 region을 RegionChartCard용으로 enrich.
  // sidos store가 로드되어야 동 이름 fallback이 동작하므로 sidosInit를 대기한다.
  useEffect(() => {
    if (!selectedGroup) {
      setGroupRegions([]);
      return;
    }
    const total = groupRegionCount(selectedGroup);
    if (total === 0) {
      setGroupRegions([]);
      return;
    }
    if (!sidosInit) return; // sidos 준비 후 재실행됨
    let cancelled = false;
    setRegionsLoading(true);
    (async () => {
      try {
        const enriched = await enrichRegions(selectedGroup, { sidos });
        if (!cancelled) setGroupRegions(enriched);
      } catch {
        if (!cancelled) setGroupRegions([]);
      } finally {
        if (!cancelled) setRegionsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedGroup, sidos, sidosInit]);

  const handleAdd = useCallback(async (e) => {
    e?.stopPropagation();
    if (!selectedGroup || !regionId) return;
    const cur = selectedGroup[field] || [];
    if (cur.includes(regionId)) return;
    const merged = [...cur, regionId];
    await dispatch(groupSvc.updateRegionGroup({ ...selectedGroup, [field]: merged }));
    toast.success(`'${selectedGroup.name}'에 추가됨`);
  }, [dispatch, selectedGroup, regionId, field]);

  const handleRemove = useCallback(async (e) => {
    e?.stopPropagation();
    if (!selectedGroup || !regionId) return;
    const filtered = (selectedGroup[field] || []).filter((id) => id !== regionId);
    await dispatch(groupSvc.updateRegionGroup({ ...selectedGroup, [field]: filtered }));
    toast.success(`'${selectedGroup.name}'에서 제거됨`);
  }, [dispatch, selectedGroup, regionId, field]);

  const handleCreateGroup = useCallback(async (e) => {
    e?.stopPropagation();
    const name = newGroupName.trim();
    if (!name) return;
    const initial = regionId ? { [field]: [regionId] } : {};
    await dispatch(groupSvc.createRegionGroup({
      name,
      desc: "",
      selectedSidos: [],
      selectedGungus: [],
      selectedDongs: [],
      ...initial,
    }));
    toast.success(`'${name}' 그룹이 생성됨`);
    setNewGroupName("");
  }, [dispatch, newGroupName, regionId, field]);

  const handleSelectGroup = useCallback((e, gid) => {
    e?.stopPropagation?.();
    setSelectedGroupId(gid);
  }, []);

  const handleDialogClose = useCallback((e, reason) => {
    e?.stopPropagation?.();
    onClose?.(e, reason);
  }, [onClose]);

  const stopProp = (e) => e.stopPropagation();

  // ─── 좌측 그룹 리스트 (데스크톱) ─────────────────────────
  const groupListPanel = (
    <Box sx={{ width: 240, borderRight: 1, borderColor: "divider", display: "flex", flexDirection: "column" }}>
      <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 700, color: "text.secondary" }}>
        그룹
      </Typography>
      <Divider />
      <List dense sx={{ flex: 1, overflowY: "auto", py: 0 }}>
        {regionGroups.map((g, idx) => {
          const isSelected = g.id === selectedGroupId;
          const isContains = (g[field] || []).includes(regionId);
          return (
            <ListItemButton
              key={g.id}
              selected={isSelected}
              onClick={(e) => handleSelectGroup(e, g.id)}
              sx={{ py: 0.8 }}
            >
              <ListItemText
                primary={
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>{g.name}</span>
                    {isContains && <MdCheck size={14} color="#FFB400" />}
                    {idx === 0 && (
                      <Typography variant="caption" sx={{ color: "primary.main", fontSize: 10 }}>· 최신</Typography>
                    )}
                  </Stack>
                }
                secondary={`${groupRegionCount(g)}개 지역`}
                primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                secondaryTypographyProps={{ fontSize: 11 }}
              />
            </ListItemButton>
          );
        })}
        {regionGroups.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1, display: "block" }}>
            그룹 불러오는 중...
          </Typography>
        )}
      </List>
      <Divider />
      <Box sx={{ p: 1, display: "flex", gap: 0.5, alignItems: "center" }}>
        <TextField
          size="small"
          placeholder="새 그룹명"
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
          onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") handleCreateGroup(e); }}
          onClick={stopProp}
          sx={{
            flex: 1,
            "& .MuiInputBase-root": { fontSize: 12 },
            "& .MuiInputBase-input": { py: 0.75, height: "auto" },
          }}
        />
        <IconButton size="small" onClick={handleCreateGroup} disabled={!newGroupName.trim()} sx={{ p: 0.5 }}>
          <MdAdd />
        </IconButton>
      </Box>
    </Box>
  );

  // ─── 모바일: 그룹 dropdown + 새 그룹 입력 ───────────────
  const mobileGroupSelect = (
    <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider", flexShrink: 0, minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", flexShrink: 0 }}>
          그룹
        </Typography>
        <Select
          size="small"
          value={selectedGroupId ?? ""}
          onChange={(e) => setSelectedGroupId(e.target.value)}
          onClick={stopProp}
          sx={{
            flex: 1,
            minWidth: 0,
            height: 32,
            fontSize: 13,
            "& .MuiSelect-select": {
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          }}
          renderValue={(value) => {
            const g = regionGroups.find((x) => x.id === value);
            return g ? g.name : "";
          }}
          MenuProps={{
            onClick: stopProp,
            PaperProps: {
              sx: { maxWidth: { xs: "calc(100vw - 32px)", md: 320 } },
            },
          }}
        >
          {regionGroups.map((g, idx) => {
            const isContains = (g[field] || []).includes(regionId);
            return (
              <MenuItem
                key={g.id}
                value={g.id}
                onClick={stopProp}
                sx={{ fontSize: 13, whiteSpace: "normal", py: 0.8 }}
              >
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexWrap: "wrap", minWidth: 0 }}>
                  <span style={{ wordBreak: "break-all" }}>{g.name}</span>
                  {isContains && <MdCheck size={14} color="#FFB400" />}
                  {idx === 0 && (
                    <Typography variant="caption" sx={{ color: "primary.main", fontSize: 10 }}>· 최신</Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, ml: 0.5 }}>
                    ({groupRegionCount(g)})
                  </Typography>
                </Stack>
              </MenuItem>
            );
          })}
        </Select>
      </Stack>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1, minWidth: 0 }}>
        <IconButton size="small" onClick={handleCreateGroup} disabled={!newGroupName.trim()} sx={{ p: 0.5, flexShrink: 0 }}>
          <MdAdd />
        </IconButton>
        <TextField
          size="small"
          placeholder="새 그룹명"
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
          onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") handleCreateGroup(e); }}
          onClick={stopProp}
          sx={{
            flex: 1,
            minWidth: 0,
            "& .MuiInputBase-root": { fontSize: 12 },
            "& .MuiInputBase-input": { py: 0.75, height: "auto" },
          }}
        />
      </Stack>
    </Box>
  );

  const dataRangeLabel =
    dataRange === DataRanges.SIDO ? "시도" :
    dataRange === DataRanges.GUNGU ? "군구" :
    dataRange === DataRanges.DONG ? "동" : "지역";

  const contentPanel = (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0 }}>
      {/* 선택된 지역 헤더 — 고정, 스크롤 X. 이름 옆에 새창열기, 아래에 카드 정보(이름 hideTitle) */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary">선택된 지역 ({dataRangeLabel})</Typography>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexWrap: "wrap" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xs: 14, md: 16 } }}>
            {regionName ?? "-"}
          </Typography>
          {regionId && dataRange && (
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); openRegionNewWindow(regionId, dataRange); }}
              sx={{ p: 0.2 }}
              aria-label="지역정보 새창으로 열기"
            >
              <RxOpenInNewWindow size={14} />
            </IconButton>
          )}
        </Stack>
        {parentName && (
          <Typography variant="caption" color="text.secondary">{parentName}</Typography>
        )}
        {/* 선택된 지역 카드 — hideTitle: 헤더에 이미 이름/링크가 있으므로 중복 방지. showStar: 재귀 방지 */}
        {selectedRegion && (
          <Box sx={{ mt: 1 }}>
            <RegionChartCard
              region={selectedRegion}
              showChart={false}
              fullWidth
              hideTitle
              showStar={false}
            />
          </Box>
        )}
      </Box>
      {selectedGroup ? (
        <>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
            spacing={{ xs: 1, sm: 0 }}
            sx={{ px: 2, py: 1.5, flexShrink: 0 }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">선택된 지역그룹</Typography>
              <Typography variant="body2">
                <span style={{ fontWeight: 700 }}>{selectedGroup.name}</span>
                {isInSelectedGroup ? " 에 등록되어 있음 ✓" : " 에 미등록"}
              </Typography>
            </Box>
            <Box sx={{ flexShrink: 0 }}>
              {isInSelectedGroup ? (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={handleRemove}
                  sx={{ fontSize: 12, fontWeight: 700, py: 0.4, px: 1.5 }}
                >
                  그룹에서 제거
                </Button>
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAdd}
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    py: 0.4,
                    px: 1.5,
                    bgcolor: "#1976D2",
                    color: "#fff",
                    "&:hover": { bgcolor: "#1565C0" },
                  }}
                >
                  이 그룹에 추가
                </Button>
              )}
            </Box>
          </Stack>
          <Divider />
          {/* "이 그룹의 지역(N)" 라벨은 스크롤 영역 밖에 고정, 카드 리스트만 스크롤 */}
          <Box sx={{ px: 1.5, pt: 1, pb: 0.5, flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              이 그룹의 지역 ({groupRegionCount(selectedGroup)})
            </Typography>
          </Box>
          <Box sx={{ px: 1.5, pb: 1, flex: 1, minHeight: 0, overflowY: "auto" }}>
            {regionsLoading ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", py: 2 }}>
                불러오는 중...
              </Typography>
            ) : groupRegions.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", py: 2 }}>
                그룹이 비어있습니다
              </Typography>
            ) : (
              <Stack spacing={1}>
                {groupRegions.map((r) => (
                  <RegionChartCard
                    key={`${r.levelLabel}-${r.id}`}
                    region={r}
                    showChart={false}
                    fullWidth
                    showStar={false}
                  />
                ))}
              </Stack>
            )}
          </Box>
        </>
      ) : (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="body2" color="text.secondary">
            그룹을 선택하세요
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      onClick={stopProp}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5, px: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>관심지역</Typography>
        <IconButton size="small" onClick={handleDialogClose}><MdClose /></IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent
        sx={{
          p: 0,
          height: { xs: "100%", md: 560 },
          minHeight: 0,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          overflow: "hidden",
        }}
        onClick={stopProp}
      >
        {isMobile ? mobileGroupSelect : groupListPanel}
        {contentPanel}
      </DialogContent>
    </Dialog>
  );
};
